import * as _ from "lodash";
import { generatePath } from "react-router-dom";
import { ScheduleData, ScheduleDataDay, EventForDay } from "src/components/compounds/ScheduleList/ScheduleList";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import * as Eden from "src/eden/content";
import { ROUTES } from "./constants/routes";
import { DATE_FORMAT, dateComparator, formatDate, getBrowserTimezone } from "./date-utils";

/**
 * Overlays realtime status (from GET /teams/:id/live) onto the schedule events so the
 * schedule reflects in-progress games. Live/new games get a LIVE game_state with the
 * current score and inning; completed games get GAME_OVER with the final score. Games
 * still scheduled (or canceled) are left untouched so they keep showing their start time.
 */
export const overlayLiveGames = (events: Event.Event[], liveGames: Eden.LiveGame[]): Event.Event[] => {
  if (!liveGames || liveGames.length === 0) return events;
  const liveByEvent = new Map(liveGames.map((g) => [g.event_id, g]));

  return events.map((event) => {
    const live = liveByEvent.get(event.id);
    if (!live || (live.status !== "live" && live.status !== "new" && live.status !== "completed")) {
      return event;
    }
    return {
      ...event,
      gameData: {
        event_id: Event.toEventID(event.id),
        game_id: Event.toGameID(event.id),
        game_state: Event.liveStatusToGameState(live.status),
        team_score: live.owning_team_score ?? 0,
        opponent_score: live.opponent_team_score ?? 0,
        last_time_to_score_ts: live.last_scoring_update ?? "",
        bats_game_state:
          live.inning != null && live.inning_half != null
            ? { inning: live.inning, inningHalf: live.inning_half }
            : undefined,
      },
    };
  });
};

type GeneratePathFunction = (teamID: Team.TeamID, event: Event.Event) => string;

const formatLocation = (event: { addressParts: string[] }) =>
  event.addressParts[0] === "No location" ? event.addressParts[0] : `at ${event.addressParts[0]}`;

export const groupEventsByMonth = (events: Event.Event[]): Event.Event[][] => {
  const out: Event.Event[][] = [];
  const sortedEvents = events.sort((a, b) => dateComparator(a.start, b.start));

  let currentMonthYear;

  for (const event of sortedEvents) {
    const eventMonthYear = formatDate(event.start, getBrowserTimezone(), DATE_FORMAT.monthAndYear);
    if (currentMonthYear !== eventMonthYear) {
      currentMonthYear = eventMonthYear;
      out.push([event]);
    } else {
      out[out.length - 1].push(event);
    }
  }

  return out;
};

const defaultGeneratePath = (teamID: Team.TeamID, event: Event.Event) => {
  // Completed/live games have a box score, so open them straight on the Box Score tab. Upcoming
  // games (no game data yet → no box score) fall back to Info, which is all that's available.
  const route = event.gameData ? ROUTES.teams.events.boxScore : ROUTES.teams.events.scheduleEvent;
  return generatePath(route, { id: teamID, eid: event.id });
};

export const mapEventsToScheduleData = (
  events: Event.Event[],
  teamID: string,
  generatePath: GeneratePathFunction = defaultGeneratePath
): ScheduleData[] => {
  const displayTimezone = getBrowserTimezone();
  const formatTime = (event: Event.Event): string => {
    return event.isFullDay ? "All Day" : formatDate(event.start, displayTimezone, DATE_FORMAT.time);
  };

  const mapEventsForDay = (events: Event.Event[], dayAndDate: string): ScheduleDataDay => ({
    date: dayAndDate.split(" ")[0],
    day: dayAndDate.split(" ")[1].toUpperCase(),
    events: _.map(
      events,
      (event: Event.Event): EventForDay => ({
        id: event.id,
        title: formatEventTitle(event),
        location: formatLocation(event),
        time: formatTime(event),
        score: formatEventScore(event),
        eventPath: generatePath(Team.toTeamID(teamID), event),
        status: event?.gameData?.game_state,
        isScrimmage: event?.subType.includes(Event.ScheduledEventSubType.Scrimmages),
        isCanceled: event.status === "canceled",
      })
    ),
  });

  const groupByDay = (events: Event.Event[]) =>
    _.groupBy(events, (e) => formatDate(e.start, displayTimezone, DATE_FORMAT.dayAndDate));

  return _.map(groupEventsByMonth(events), (eventsForAMonth) => ({
    monthYear: formatDate(eventsForAMonth[0].start, displayTimezone, DATE_FORMAT.monthAndYear),
    monthAbbreviation: formatDate(eventsForAMonth[0].start, displayTimezone, DATE_FORMAT.monthAbbreviation),
    days: _.map(groupByDay(eventsForAMonth), mapEventsForDay),
  }));
};

export const formatEventTitle = (event: Event.Event): string => {
  if (event.eventType === "game") {
    const opponent = _.get(event, ["opponentTeam", "name"], "TBD");
    const prefix = event.homeAway === "away" ? "@" : "vs.";
    return `${prefix} ${opponent}`;
  } else {
    return event.title || "TBD";
  }
};

// Summarizes an in-progress game ("@ Tigers · 3-13 · Bottom 5") for a live card/placeholder.
// Each part is optional so a freshly-started game (no score/inning yet) still reads cleanly.
export const formatLiveMeta = (game: Eden.LiveGame): string | undefined => {
  const parts: string[] = [];
  const opponent = game.opponent_name
    ? `${game.home_away === "away" ? "@" : "vs."} ${game.opponent_name}`
    : game.title ?? undefined;
  if (opponent) parts.push(opponent);
  if (game.owning_team_score != null && game.opponent_team_score != null) {
    parts.push(`${game.owning_team_score}-${game.opponent_team_score}`);
  }
  if (game.inning != null && game.inning_half != null) {
    parts.push(`${game.inning_half === "top" ? "Top" : "Bottom"} ${game.inning}`);
  }
  return parts.length ? parts.join(" · ") : undefined;
};

export const formatEventScore = (event: Event.Event): string | undefined => {
  const score = event.gameData?.team_score;
  const opp_score = event.gameData?.opponent_score;
  const game_state = event.gameData?.game_state;
  if (score === undefined || opp_score === undefined || game_state === undefined) return;

  const score_text = `${score}-${opp_score}`;
  if (game_state === Event.GameState.GAME_OVER) {
    if (score > opp_score) return `W ${score_text}`;
    if (opp_score > score) return `L ${score_text}`;
    if (opp_score === score) return `T ${score_text}`;
  }
  return score_text;
};
